import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, IndianRupee, CreditCard, Banknote } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { shopAPI } from '@/lib/api';
import { useCart } from '@/context/CartContext';

export default function Checkout() {
  const { shopSlug } = useParams();
  const navigate = useNavigate();
  const { cart, getTotal, clearCart } = useCart();

  const [loading, setLoading] = useState(false);
  const [shop, setShop] = useState(null);
  const [shopLoading, setShopLoading] = useState(true);

  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [showQR, setShowQR] = useState(false);

  // Mobile-only Click-to-Pay helpers
  const [isMobileScreen, setIsMobileScreen] = useState(false);

  // Click-to-pay UI + flow
  const [showClickPayOverlay, setShowClickPayOverlay] = useState(false);
  const [clickPayInProgress, setClickPayInProgress] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    customer_upi_id: ''
  });

  // timers/guards
  const autoPlacedRef = useRef(false);
  const overlayTimerRef = useRef(null);
  const autoSubmitTimerRef = useRef(null);
  const openAppsTimerRef = useRef(null);
  const orderPlacedRef = useRef(false);

  useEffect(() => {
    fetchShop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSlug]);

  useEffect(() => {
    const updateMobileScreen = () => {
      try {
        const mq = window.matchMedia('(max-width: 768px)');
        const touch = (navigator.maxTouchPoints || 0) > 0;
        setIsMobileScreen(mq.matches && touch);
      } catch {
        setIsMobileScreen(false);
      }
    };

    updateMobileScreen();
    window.addEventListener('resize', updateMobileScreen);
    return () => window.removeEventListener('resize', updateMobileScreen);
  }, []);

  useEffect(() => {
    return () => {
      if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
      if (autoSubmitTimerRef.current) clearTimeout(autoSubmitTimerRef.current);
      if (openAppsTimerRef.current) clearTimeout(openAppsTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!shopLoading && cart.length === 0 && !orderPlacedRef.current) {
      navigate(`/shop/${shopSlug}`);
    }
  }, [cart.length, shopLoading, navigate, shopSlug]);

  const fetchShop = async () => {
    try {
      setShopLoading(true);
      const response = await shopAPI.getShop(shopSlug);
      setShop(response.data?.seller || null);
    } catch (error) {
      console.error('Failed to fetch shop:', error);
      setShop(null);
    } finally {
      setShopLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const itemsTotal = useMemo(() => Number(getTotal() || 0), [getTotal, cart]);

  const pricing = useMemo(() => {
    const deliveryAmount = Number(shop?.delivery_amount ?? shop?.delivery_charge ?? 0);
    const freeFrom = Number(
      shop?.free_delivery_start_range ??
        shop?.free_delivery_min_amount ??
        0
    );

    const isFreeDelivery = freeFrom > 0 && itemsTotal >= freeFrom;
    const deliveryFee = isFreeDelivery ? 0 : deliveryAmount;
    const payableTotal = Number(itemsTotal + deliveryFee);

    return {
      deliveryAmount,
      freeFrom,
      isFreeDelivery,
      deliveryFee,
      payableTotal
    };
  }, [shop, itemsTotal]);

  const { deliveryAmount, freeFrom, isFreeDelivery, deliveryFee, payableTotal } = pricing;

  // ---- UPI QR (scan) uses payable total ----
  const generateUpiUri = useCallback(() => {
    if (!shop) return '';
    const params = new URLSearchParams({
      pa: shop.upi_id,
      pn: shop.business_name,
      am: payableTotal.toString(),
      cu: 'INR',
      tn: `Order from ${shop.business_name}`
    });
    return `upi://pay?${params.toString()}`;
  }, [shop, payableTotal]);

  // ---- Customer UPI ID rules ----
  const normalizeCustomerUpiId = useCallback(() => {
    const raw = String(formData.customer_upi_id || '').trim();
    if (raw.length > 0) return raw;
    return String(formData.customer_phone || '').trim();
  }, [formData.customer_upi_id, formData.customer_phone]);

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const openUpiChooser = () => {
    try {
      window.location.href = 'upi://pay?cu=INR';
      return true;
    } catch {
      return false;
    }
  };

  const submitOrderNow = useCallback(async () => {
    if (loading || shopLoading || !shop) return;

    setLoading(true);
    try {
      const orderData = {
        seller_id: shop?.id,
        ...formData,
        customer_email: String(formData.customer_email || '').trim(),
        customer_upi_id: normalizeCustomerUpiId(),
        payment_method: paymentMethod,
        items: cart.map((item) => ({
          product_id: item.id,
          quantity: item.quantity,
          price: item.final_price
        })),
        total_amount: payableTotal
      };

      const response = await shopAPI.createOrder(shopSlug, orderData);
	  orderPlacedRef.current = true;
      clearCart();
      navigate(`/shop/${shopSlug}/order-confirmation`, {
        state: { orderId: response.data.order_id }
      });
    } catch (error) {
      toast.error('Failed to place order');
      autoPlacedRef.current = false;
      setClickPayInProgress(false);
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    shopLoading,
    shop,
    formData,
    normalizeCustomerUpiId,
    paymentMethod,
    cart,
    payableTotal,
    shopSlug,
    clearCart,
    navigate
  ]);

  const handleClickToPay = useCallback(
    async (e) => {
      e.preventDefault();

      if (loading || clickPayInProgress || shopLoading) return;
      if (!shop?.upi_id) {
        toast.error('Seller UPI ID not found. Please scan QR.');
        return;
      }
      if (autoPlacedRef.current) return;

      autoPlacedRef.current = true;
      setClickPayInProgress(true);

      if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
      if (autoSubmitTimerRef.current) clearTimeout(autoSubmitTimerRef.current);
      if (openAppsTimerRef.current) clearTimeout(openAppsTimerRef.current);

      await copyToClipboard(shop.upi_id);
      setShowQR(true);
      setShowClickPayOverlay(true);

      overlayTimerRef.current = setTimeout(() => {
        setShowClickPayOverlay(false);

        openAppsTimerRef.current = setTimeout(() => {
          const ok = openUpiChooser();
          if (!ok) {
            toast.info('Open your UPI app and paste the copied UPI ID.');
          }
        }, 50);
      }, 10000);

      autoSubmitTimerRef.current = setTimeout(async () => {
        try {
          if (!autoPlacedRef.current) return;

          setLoading(true);

          const orderData = {
            seller_id: shop?.id,
            ...formData,
            customer_email: String(formData.customer_email || '').trim(),
            customer_upi_id: normalizeCustomerUpiId(),
            payment_method: 'upi_clicktopay',
            items: cart.map((item) => ({
              product_id: item.id,
              quantity: item.quantity,
              price: item.final_price
            })),
            total_amount: payableTotal
          };

          const response = await shopAPI.createOrder(shopSlug, orderData);
		  orderPlacedRef.current = true;
          clearCart();
          navigate(`/shop/${shopSlug}/order-confirmation`, {
            state: { orderId: response.data.order_id }
          });
        } catch (err) {
          toast.error('Order could not be placed. Please try again.');
          autoPlacedRef.current = false;
          setClickPayInProgress(false);
        } finally {
          setLoading(false);
        }
      }, 30000);
    },
    [
      loading,
      clickPayInProgress,
      shopLoading,
      shop,
      formData,
      payableTotal,
      cart,
      shopSlug,
      clearCart,
      navigate,
      normalizeCustomerUpiId
    ]
  );

  const handleManualPaymentConfirm = useCallback(async () => {
    if (autoSubmitTimerRef.current) clearTimeout(autoSubmitTimerRef.current);
    if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
    if (openAppsTimerRef.current) clearTimeout(openAppsTimerRef.current);

    setShowClickPayOverlay(false);
    setClickPayInProgress(false);

    try {
      setLoading(true);
      const orderData = {
        seller_id: shop?.id,
        ...formData,
        customer_email: String(formData.customer_email || '').trim(),
        customer_upi_id: normalizeCustomerUpiId(),
        payment_method: autoPlacedRef.current ? 'upi_clicktopay' : paymentMethod,
        items: cart.map((item) => ({
          product_id: item.id,
          quantity: item.quantity,
          price: item.final_price
        })),
        total_amount: payableTotal
      };

      const response = await shopAPI.createOrder(shopSlug, orderData);
	  orderPlacedRef.current = true;
      clearCart();
      navigate(`/shop/${shopSlug}/order-confirmation`, {
        state: { orderId: response.data.order_id }
      });
    } catch (err) {
      toast.error('Failed to place order');
      autoPlacedRef.current = false;
      setClickPayInProgress(false);
    } finally {
      setLoading(false);
    }
  }, [
    shop?.id,
    formData,
    normalizeCustomerUpiId,
    paymentMethod,
    cart,
    payableTotal,
    shopSlug,
    clearCart,
    navigate
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (shopLoading) {
      toast.error('Please wait, delivery charges are loading...');
      return;
    }

    if (!formData.customer_name || !formData.customer_phone || !formData.customer_address) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!String(formData.customer_email || '').trim()) {
      toast.error('Please enter your email');
      return;
    }

    if (paymentMethod === 'upi') {
      setShowQR(true);
      return;
    }

    await submitOrderNow();
  };

  if (shopLoading && cart.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="checkout-page">
      {showClickPayOverlay && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-green-600" />
          <div className="relative w-full max-w-md mx-auto px-6 text-center text-white">
            <div className="space-y-2">
              <div className="text-lg font-semibold opacity-95">Pay</div>
              <div className="text-5xl font-extrabold tracking-tight">₹{payableTotal}</div>
              <div className="text-sm opacity-90">
                to <span className="font-mono">{shop?.upi_id}</span>
              </div>
              <div className="text-base font-semibold mt-3">
                UPI ID copied. Paste it in your installed UPI app and pay.
              </div>
              <div className="text-[10px] mt-3 text-red-200">
                Amount mismatch may lead to order cancellation.
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link
            to={`/shop/${shopSlug}/cart`}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Cart</span>
          </Link>
          <h1 className="font-heading font-bold">Checkout</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {showQR ? (
          <div className="max-w-md mx-auto text-center">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading">Scan to Pay</CardTitle>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="bg-white p-6 rounded-xl inline-block">
                  <QRCodeSVG value={generateUpiUri()} size={220} level="H" data-testid="upi-qr-code" />
                </div>

                {isMobileScreen && (
                  <div className="space-y-2 -mt-2">
                    <a
                      href="#"
                      onClick={handleClickToPay}
                      className="text-sm font-medium text-primary underline underline-offset-4 hover:opacity-90"
                    >
                      Or click to pay
                    </a>

                    <p className="text-xs text-muted-foreground">
                      Seller UPI ID will be copied. Paste it in your UPI app and enter the amount manually.
                    </p>
                  </div>
                )}

                <div className="space-y-1">
                  <p className="text-2xl font-heading font-bold flex items-center justify-center text-primary">
                    <IndianRupee className="h-6 w-6" />
                    {payableTotal}
                  </p>

                  <p className="text-sm text-muted-foreground">
                    Includes delivery: {shopLoading ? 'Loading...' : deliveryFee === 0 ? '₹0' : `₹${deliveryFee}`}
                  </p>

                  <p className="text-sm text-muted-foreground mt-2">Pay to: {shop?.upi_id}</p>
                </div>

                <div className="space-y-3">
                  <Button
                    className="w-full rounded-full"
                    onClick={handleManualPaymentConfirm}
                    disabled={loading || shopLoading}
                    data-testid="confirm-payment-btn"
                  >
                    {loading ? 'Processing...' : 'I have completed payment'}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setShowQR(false);
                      autoPlacedRef.current = false;
                      setClickPayInProgress(false);
                      if (autoSubmitTimerRef.current) clearTimeout(autoSubmitTimerRef.current);
                      if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
                      if (openAppsTimerRef.current) clearTimeout(openAppsTimerRef.current);
                      setShowClickPayOverlay(false);
                    }}
                  >
                    Back
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground">
                  Note: Payment verification is manual. The seller will confirm your payment.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="font-heading">Delivery Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="customer_name">Full Name *</Label>
                        <Input
                          id="customer_name"
                          name="customer_name"
                          value={formData.customer_name}
                          onChange={handleInputChange}
                          placeholder="Enter your full name"
                          required
                          data-testid="customer-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="customer_phone">Phone Number *</Label>
                        <Input
                          id="customer_phone"
                          name="customer_phone"
                          value={formData.customer_phone}
                          onChange={handleInputChange}
                          placeholder="10-digit mobile number"
                          required
                          data-testid="customer-phone-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="customer_email">Email *</Label>
                      <Input
                        id="customer_email"
                        name="customer_email"
                        type="email"
                        value={formData.customer_email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        required
                        data-testid="customer-email-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="customer_address">Delivery Address *</Label>
                      <Textarea
                        id="customer_address"
                        name="customer_address"
                        value={formData.customer_address}
                        onChange={handleInputChange}
                        placeholder="Enter your full delivery address"
                        rows={3}
                        required
                        data-testid="customer-address-input"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="font-heading">Payment Method</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                      <label
                        className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                          paymentMethod === 'cod' ? 'border-primary bg-primary/5' : 'border-border'
                        }`}
                      >
                        <RadioGroupItem value="cod" id="cod" data-testid="cod-radio" />
                        <Banknote className="h-5 w-5 text-secondary" />
                        <div className="flex-1">
                          <p className="font-medium">Cash on Delivery</p>
                          <p className="text-sm text-muted-foreground">Pay when you receive</p>
                        </div>
                      </label>

                      <label
                        className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                          paymentMethod === 'upi' ? 'border-primary bg-primary/5' : 'border-border'
                        }`}
                      >
                        <RadioGroupItem value="upi" id="upi" data-testid="upi-radio" />
                        <CreditCard className="h-5 w-5 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium">Pay via UPI</p>
                          <p className="text-sm text-muted-foreground">Scan QR code to pay</p>
                        </div>
                      </label>
                    </RadioGroup>

                    {paymentMethod === 'upi' && (
                      <div className="space-y-2 pt-2 border-t border-border">
                        <Label htmlFor="customer_upi_id">Your UPI ID *</Label>
                        <Input
                          id="customer_upi_id"
                          name="customer_upi_id"
                          value={formData.customer_upi_id}
                          onChange={handleInputChange}
                          placeholder="your UPI ID or mobile number"
                          data-testid="customer-upi-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Leave blank if you want to use your phone number for verification.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full rounded-full btn-glow"
                  disabled={loading || shopLoading}
                  data-testid="place-order-btn"
                >
                  {shopLoading
                    ? 'Loading charges...'
                    : loading
                    ? 'Processing...'
                    : paymentMethod === 'upi'
                    ? 'Continue to Payment'
                    : 'Place Order'}
                </Button>
              </form>
            </div>

            <div>
              <Card className="border-border/50 sticky top-24">
                <CardHeader>
                  <CardTitle className="font-heading">Order Summary</CardTitle>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-2 text-sm">
                    {cart.map((item) => (
                      <div key={item.id} className="flex justify-between">
                        <span className="text-muted-foreground">
                          {item.name} × {item.quantity}
                        </span>
                        <span>₹{item.final_price * item.quantity}</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-border pt-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>₹{itemsTotal}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Delivery {freeFrom > 0 ? `(Free from ₹${freeFrom})` : ''}
                      </span>
                      <span>{shopLoading ? 'Loading...' : deliveryFee === 0 ? '₹0' : `₹${deliveryFee}`}</span>
                    </div>

                    {!shopLoading && freeFrom > 0 && !isFreeDelivery && deliveryAmount > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Add ₹{Math.max(0, freeFrom - itemsTotal)} more for free delivery.
                      </p>
                    )}

                    {!shopLoading && freeFrom > 0 && isFreeDelivery && (
                      <p className="text-xs text-muted-foreground">You’ve unlocked free delivery 🎉</p>
                    )}

                    <div className="pt-2 flex justify-between font-heading font-bold text-lg">
                      <span>Total</span>
                      <span className="flex items-center text-primary">
                        <IndianRupee className="h-5 w-5" />
                        {shopLoading ? '...' : payableTotal}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}